import type { AccordionProps } from '@magicschool/spellbook/components/Accordion';
import { Icon, type IconProps } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Text, type TextProps } from '@magicschool/spellbook/components/Text';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

export type AccordionItemProps = ComponentProps<typeof AccordionPrimitive.Item> &
  Pick<AccordionProps, 'iconPosition' | 'variant' | 'fillHeight'>;

const itemVariants = cva('', {
  variants: {
    variant: {
      stacking: 'border-b border-b-border-default last:border-b-0',
      card: 'rounded-md',
    },
  },
  defaultVariants: {
    variant: 'stacking',
  },
});

const accordionTriggerVariants = cva(
  [
    'group',
    'w-full',
    'cursor-pointer',
    'flex',
    'items-center',
    'gap-2',
    'py-2',
    'px-4',
    'min-h-control-h-sm',
    'transition-all',
    'outline-none',
    'text-default-text',
    'text-start',
    'focus-visible:border-ring',
    'focus-visible:ring',
    'focus-visible:ring-focus-ring',
    'focus-visible:z-1',
    'disabled:pointer-events-none',
    'disabled:bg-disabled-background',
    'disabled:text-disabled-foreground',
  ],
  {
    variants: {
      iconPosition: {
        start: '',
        end: 'flex-row-reverse',
      },
      variant: {
        stacking: ['hover:bg-surface-transparent-1', 'focus-visible:bg-surface-transparent-1'],
        card: [
          'bg-surface-background-0',
          'hover:bg-surface-background-2',
          'active:bg-surface-background-3',
          'p-4',
          'border-1',
          'border-border-default',
          'hover:border-border-hover',
          'transition-all',
          'rounded-md',
          'data-[state=open]:rounded-b-none',
        ],
      },
    },
    defaultVariants: {
      iconPosition: 'start',
      variant: 'stacking',
    },
  },
);

const accordionContentVariants = cva('px-4 pb-4 text-default-text', {
  variants: {
    variant: {
      stacking: 'pt-2',
      card: 'bg-surface-background-0 pt-4 rounded-md rounded-t-none border-1 border-t-0 border-border-default',
    },
  },
  defaultVariants: {
    variant: 'stacking',
  },
});

export const AccordionItemSlot = Slot<AccordionItemProps>({
  displayName: 'Accordion.Item',
  preventOverride: true,
  renderDefault: ({ children, className, iconPosition, variant, fillHeight, ...props }) => {
    const { slots } = useSlots(children, {
      trigger: { Component: AccordionTriggerSlot, props: { iconPosition, variant } },
      content: { Component: AccordionContentSlot, props: { variant, fillHeight } },
    });

    return (
      <AccordionPrimitive.Item
        data-spellbook-name="accordion-item"
        className={cn(itemVariants({ variant }), fillHeight && 'flex-1 min-h-0 flex flex-col', className)}
        {...props}
      >
        {slots.trigger}
        <AccordionPrimitive.Content
          data-spellbook-name="accordion-content"
          className={cn(
            'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
            fillHeight && 'flex-1 min-h-0 flex flex-col',
          )}
        >
          {slots.content}
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    );
  },
});

export const AccordionTriggerSlot = Slot<TextProps & Pick<AccordionProps, 'iconPosition' | 'variant'>>({
  displayName: 'Accordion.Trigger',
  preventOverride: true,
  renderDefault: ({ children, className, iconPosition, variant, ...props }) => {
    const { slots } = useSlots(children, {
      openIcon: { Component: AccordionOpenIconSlot },
      closeIcon: { Component: AccordionCloseIconSlot },
      label: { Component: AccordionTriggerLabelSlot, props },
      description: { Component: AccordionTriggerDescriptionSlot, props },
      triggerTrailingContent: { Component: AccordionTriggerTrailingContent, props },
    });

    return (
      <AccordionPrimitive.Header asChild>
        <AccordionPrimitive.Trigger
          data-spellbook-name="accordion-trigger"
          className={cn(accordionTriggerVariants({ iconPosition, variant }), className)}
        >
          {slots.openIcon}
          {slots.closeIcon}
          <div className="flex flex-col w-full min-w-0">
            {slots.label}
            {slots.description}
          </div>
          {slots.triggerTrailingContent}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  },
});

export const AccordionTriggerLabelSlot = Slot<TextProps>({
  displayName: 'Accordion.TriggerLabel',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => (
    <Text data-spellbook-name="accordion-trigger-label" className={cn('flex-1', className)} element="p" variant="heading-5" {...props}>
      {children}
    </Text>
  ),
});

export const AccordionTriggerDescriptionSlot = Slot<TextProps>({
  displayName: 'Accordion.TriggerDescription',
  preventOverride: true,
  renderDefault: ({ children, className, ...props }) => (
    <Text
      data-spellbook-name="accordion-trigger-description"
      className={cn('text-default-text-muted text-sm', className)}
      element="p"
      variant="body-2"
      {...props}
    >
      {children}
    </Text>
  ),
});

export const AccordionContentSlot = Slot<ComponentProps<'div'> & Pick<AccordionProps, 'variant' | 'fillHeight'>>({
  displayName: 'Accordion.Content',
  renderDefault: ({ children, className, variant, fillHeight, ...props }) => (
    <div className={cn(accordionContentVariants({ variant }), fillHeight && 'flex flex-col flex-1 min-h-0', className)} {...props}>
      {children}
    </div>
  ),
});

export const AccordionOpenIconSlot = Slot<IconProps>({
  displayName: 'Accordion.OpenIcon',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({ className, name = 'chevron-down', size = 'sm', ...props }) => (
    <Icon
      data-spellbook-name="accordion-open-icon"
      className={cn(
        'group-data-[state=open]:hidden group-data-[disabled]:stroke-disabled-foreground not-group-data-[disabled]:stroke-default-icon',
        className,
      )}
      name={name}
      size={size}
      {...props}
    />
  ),
});

export const AccordionCloseIconSlot = Slot<IconProps>({
  displayName: 'Accordion.CloseIcon',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({ className, name = 'chevron-up', size = 'sm', ...props }) => (
    <Icon
      data-spellbook-name="accordion-close-icon"
      className={cn(
        'group-data-[state=closed]:hidden group-data-[disabled]:stroke-disabled-foreground not-group-data-[disabled]:stroke-default-icon',
        className,
      )}
      name={name}
      size={size}
      {...props}
    />
  ),
});

export const AccordionTriggerTrailingContent = Slot<{ children: React.ReactNode }>({
  displayName: 'Accordion.TriggerTrailingContent',
  renderDefault: ({ children, ...props }) => (
    <div data-spellbook-name="accordion-trigger-trailing-content" {...props}>
      {children}
    </div>
  ),
});
